import React, { useState, useRef, useEffect, useContext } from "react";
import { cn, getStashed } from "../../lib/helpers";
import { FloatyIcon, validateDomain } from "./snowflake/play-finder";
import {
  faHandshake, faMagnifyingGlassChart, faMoneyBill1Wave, faPeopleGroup, faRocket, faThumbsUp
} from "@fortawesome/free-solid-svg-icons";
import * as SampleData from "../../../static/data/company-lookup.json";
import { freeEmailDomains } from "../../../static/data/free-email-domains";
import { SiteContext } from "../global/site-context";
import { getSimpleDate, countChatbotMessages, getChatbotGoal, countChatbotSessions, getChatbotTranscript, getChatbotPath } from "../../lib/client-attribution";
import { ATTRIBUTION_PARAM } from "./snowflake/navigation";

const FAKE_API_DELAY = 3000;
export const WORKFLOW_ID = "883e7d10-d9c3-426c-90bc-96181478520c";
export const READINESS_SCORE_WORKFLOW_ID = "907d83fb-49a0-4277-bad4-4003f8b3e371";
//const TEST_MODE = "https://deploy-preview-629--www-common-room.netlify.app/.netlify/functions/lookup";
const TEST_MODE = false;
const CHILIPIPER_ROUTER_ID = "zcr-inbound-demo-request";
const MAX_LOOKUPS = 5;
const MAX_LOOKUP_STASH = "domain-lookups";
const BUTTON_NOT_READY = "Request Demo";
const BUTTON_SUBMIT = "Get Started";

const WORKFLOW_VERSION = "83";

export const TRACKABLE_FIELDS = [
  "source",
  "campaign",
  "medium",
  "content",
  "term",
];

const ORG_STATES = {
  NULL: "NULL",
  KNOWN: "KNOWN",
  UNKNOWN: "UNKNOWN"
};

const ID_STATES = {
  NULL: "NULL",
  KNOWN: "KNOWN",
  UNKNOWN: "UNKNOWN"
};

export const EMAIL_STATES = {
  NULL: "NULL",
  VALID: "VALID",
  INVALID: "INVALID"
};

export const ROLE_OPTIONS = [
  "Business Development",
  "Sales",
  "Operations",
  "Marketing",
  "Other"
];

const COMPANY_SIZE_OPTIONS = [
  "1 - 75",
  "76 - 250",
  "251 - 750",
  "751 - 1000",
  "1001 - 1500",
  "1501 - 2500",
  "2501+",
];


export const mapTeamSizeToCompanySize = (size) => {
  if (!size) return "";
  const numericSize = parseInt(size.toString().replace(/[^\d]/g, ''));

  if (isNaN(numericSize)) return "";

  if (numericSize <= 50) return "1 - 75";
  if (numericSize <= 250) return "76 - 250";
  if (numericSize <= 750) return "251 - 750";
  if (numericSize <= 1000) return "751 - 1000";
  if (numericSize <= 1500) return "1001 - 1500";
  if (numericSize <= 2500) return "1501 - 2500";
  return "2501+";
};

export const isDomainFree = (domain) => {
  if (!domain) return false;
  return freeEmailDomains.includes(domain.toLowerCase());
};

// @todo Add "Open Source" when true
// @todo Add "AI forward" when > 3
export const FIELD_LABELS = {
  "Industry": "Industry",
  "Key Offering": "Service",
  "Team": "Team",
  "Funding": "Funding",
  "Social": "Social",
  "Web Traffic": "Traffic",
};

// Display field mappings - prefer UI versions for human-readable formatting
export const DISPLAY_FIELD_LABELS = {
  "Industry": "Industry",
  "Key Offering": "Service",
  "Team": "Team",
  "Funding UI": "Funding",
  "Social UI": "Social",
  "Web Traffic UI": "Traffic",
};


export const DemoUX = ({ section, skipIcons, isDownPage }) => {
  const site = useContext(SiteContext);
  const storage = site.metrics.analytics.storage;

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [referredBy, setReferredBy] = useState("");
  // 0 = email, 1 = role, 2 = firstName, 3 = lastName, 4 = referredBy, 5 = companySize
  const [focusedField, setFocusedField] = useState(0);
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState("");
  const [orgState, setOrgState] = useState(ORG_STATES.NULL);
  const [identityState, setIdentityState] = useState(ID_STATES.NULL);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [lookupOrg, setLookupOrg] = useState({});
  const [lookupScore, setLookupScore] = useState(null);
  const [emailValid, setEmailValid] = useState(EMAIL_STATES.NULL);
  const [hasEmailParam, setHasEmailParam] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const emailRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const roleRef = useRef(null);
  const companySizeRef = useRef(null);
  const companySizeWrapperRef = useRef(null);
  const referredByRef = useRef(null);
  const submitRef = useRef(null);

  const isRoomie = !!storage.getItem("roomie") || site.isAdmin;

  // Load ChiliPiper script on mount so it's ready when the form is submitted
  useEffect(() => {
    if (!document.getElementById('chilipiper-concierge')) {
      const script = document.createElement('script');
      script.id = 'chilipiper-concierge';
      script.src = 'https://commonroom.chilipiper.com/concierge-js/cjs/concierge.js';
      script.crossOrigin = 'anonymous';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if ((storage.getItem(MAX_LOOKUP_STASH) > MAX_LOOKUPS) && !isRoomie) {
      doSetOrgState(ORG_STATES.UNKNOWN, companySize);
    }

    // Check for email parameter in URL
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('e');
    if (emailParam) {
      setHasEmailParam(true);
      try {
        setEmail(atob(emailParam));
        // Trigger blur after a short delay to ensure state is updated
        setTimeout(() => {
          handleEmailBlur();
          if (roleRef.current) {
            roleRef.current.focus();
            setFocusedField(1);
          }
        }, 100);
      } catch (error) {
        console.error('Failed to decode email parameter:', error);
      }
    }
  }, []);

  useEffect(() => {
    const newReady = email && emailValid === EMAIL_STATES.VALID &&
      firstName && lastName &&
      role && companySize && referredBy;

    setReady(newReady);
  }, [
    email, emailValid, firstName, lastName, role,
    companySize, referredBy, identityState
  ]);

  const handleEmailBlur = async () => {
    if (!email) return;

    const domain = email.split('@')[1]?.toLowerCase();
    if (!validateDomain(domain)) {
      console.debug("Invalid domain:", domain);
      setEmailValid(EMAIL_STATES.INVALID);
      setMessage("Invalid email.");
      return;
    }

    if (isDomainFree(domain)) {
      console.debug("Free email service detected:", domain);
      setEmailValid(EMAIL_STATES.INVALID);
      setMessage("Use a business email from your company domain.");
      return;
    }

    const tld = domain.split('.').pop();
    if (["edu", "gov"].includes(tld)) {
      console.debug("Ineligible domain type:", domain);
      setEmailValid(EMAIL_STATES.INVALID);
      setMessage("Educational and government organizations are not eligible.");
      return;
    }

    console.debug("Valid email domain:", domain);
    setEmailValid(EMAIL_STATES.VALID);
    setMessage("");

    if ((storage.getItem(MAX_LOOKUP_STASH) > MAX_LOOKUPS) && !isRoomie) {
      doSetOrgState(ORG_STATES.UNKNOWN, companySize);
      console.debug("Max lookups reached.");
      return;
    }

    const [companyResponse, scoreResponse] = await Promise.allSettled([
      fetch(TEST_MODE || "/.netlify/functions/airops", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          inputs: {
            email: email,
            domain: domain,
            workflowId: WORKFLOW_ID,
            token: site.buildToken,
            ...(WORKFLOW_VERSION && { version_slug: WORKFLOW_VERSION }),
          },
        }),
      }),
      fetch("/.netlify/functions/airops", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          inputs: {
            email: email,
            domain: domain,
            workflowId: READINESS_SCORE_WORKFLOW_ID,
            token: site.buildToken,
          },
        }),
      }),
    ]);

    // Handle company lookup result
    if (companyResponse.status === "fulfilled") {
      try {
        const response = companyResponse.value;
        const data = await response.json();
        console.debug("Lookup response:", data);
        if (response.ok) {
          console.debug("Company found.");
          const mappedCompanySize = mapTeamSizeToCompanySize(data.result?.Team);
          setLookupOrg(data.result);
          setCompanySize(mappedCompanySize);
          doSetOrgState(ORG_STATES.KNOWN, mappedCompanySize);
          console.debug("Post-API org data:", { mappedCompanySize });
        } else {
          console.debug("Company not found.");
          doSetOrgState(ORG_STATES.UNKNOWN, mapTeamSizeToCompanySize(data.result?.Team));
        }
      } catch (error) {
        console.error("Error parsing company lookup response:", error);
        doSetOrgState(ORG_STATES.UNKNOWN, mapTeamSizeToCompanySize(SampleData?.Team));
      }
    } else {
      console.error("Error calling company lookup endpoint:", companyResponse.reason);
      if (TEST_MODE) {
        setTimeout(() => {
          console.warn("Using sample data for demo purposes.");
          setLookupOrg(SampleData);
          setCompanySize(mapTeamSizeToCompanySize(SampleData?.Team));
          doSetOrgState(ORG_STATES.KNOWN, mapTeamSizeToCompanySize(SampleData?.Team));
        }, FAKE_API_DELAY);
      } else {
        doSetOrgState(ORG_STATES.UNKNOWN, mapTeamSizeToCompanySize(SampleData?.Team));
      }
    }

    // Handle readiness score result
    if (scoreResponse.status === "fulfilled") {
      try {
        const response = scoreResponse.value;
        const data = await response.json();
        if (response.ok) {
          setLookupScore(data.result?.score ?? null);
          console.debug("Readiness score:", data.result?.score);
        }
      } catch (error) {
        console.error("Error parsing readiness score response:", error);
      }
    } else {
      console.error("Error calling readiness score endpoint:", scoreResponse.reason);
    }

    storage.setItem(MAX_LOOKUP_STASH, (storage.getItem(MAX_LOOKUP_STASH) || 0) + 1);
  }

  const handleKeyDown = (e, currentIndex) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (currentIndex === 0 && roleRef.current) {
        roleRef.current.focus();
        setFocusedField(1);
      } else if (currentIndex === 1 && identityState === ID_STATES.NULL && firstNameRef.current) {
        firstNameRef.current.focus();
        setFocusedField(2);
      } else if (currentIndex === 1 && identityState === ID_STATES.KNOWN && submitRef.current) {
        // If identity is known, go directly from role to submit
        submitRef.current.click();
      } else if (currentIndex === 2 && lastNameRef.current && identityState === ID_STATES.NULL) {
        lastNameRef.current.focus();
        setFocusedField(3);
      } else if (currentIndex === 3 && referredByRef.current && identityState === ID_STATES.NULL) {
        referredByRef.current.focus();
        setFocusedField(4);
      } else if (currentIndex === 4 && companySizeRef.current && identityState === ID_STATES.NULL) {
        companySizeRef.current.focus();
        setFocusedField(5);
      } else if (currentIndex === 5 && submitRef.current) {
        submitRef.current.click();
      }
    }
  };

  const handleFocus = (fieldIndex) => {
    setFocusedField(fieldIndex);
  };

  const deployCalendar = (orgData) => {
    const org = orgData || lookupOrg;
    if (window.ChiliPiper) {
      try {
        setCalendarVisible(true);
        window.ChiliPiper.submit("commonroom", CHILIPIPER_ROUTER_ID, {
          domElement: '#chilipiper-calendar-container',
          formId: 'startRouter',
          map: true,
          lead: {
            "email": email,
            "firstname": firstName,
            "lastname": lastName,
            "role": role,
            "employee_count___range": companySize,
            "ai_qualified": org?.Qualify,
            "company_name": org?.Name,
            "domain": org?.Domain,
            "company_offering": org?.["Key Offering"],
            "company_industry": org?.Industry,
            "company_team": org?.Team,
            "company_ai_score": org?.AI,
            "company_open_source": org?.["Open Source"],
            "company_social": org?.Social,
            "company_funding": org?.Funding,
            "company_web_traffic": org?.["Web Traffic"],
            "readiness_score": lookupScore,
            "chatbot_engaged": String(countChatbotMessages()),
            "chatbot_sessions": String(countChatbotSessions()),
            "chatbot_goal": getChatbotGoal() || "",
            "chatbot_transcript": getChatbotTranscript(),
            "chatbot_path": getChatbotPath() || "",
          },
          onSuccess: () => { setIsSuccess(true); },
          onError: () => { },
          onClose: () => { },
          onRouted: () => { },
          onDisqualified: () => { setCalendarVisible(false); },
        });
      } catch (err) {
        console.error("Calendar initialization error:", err);
      }
    } else {
      // Script not loaded yet, retry
      setTimeout(() => deployCalendar(org), 500);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Demo UX Form submitted:", {
      email, firstName, lastName, role, companySize, referredBy
    });
    setFormSubmitted(true);
    storage.setItem("user.email", email);
    site.metrics.logEvent({
      label: "send",
      category: "button_click",
      action: "contact",
    });

    // Identify with HubSpot tracking script so the browser session is tied to this contact
    if (window._hsq) {
      window._hsq.push(["identify", { email }]);
    }

    // Server-side submission to HubSpot as a fallback for ad blockers / script failures.
    // hutk ties this API submission to the same HubSpot contact as the tracking cookie.
    const hutk = document.cookie.split(";").find((c) => c.trim().startsWith("hubspotutk="))?.split("=")[1] || "";
    const params = new URLSearchParams(window.location.search);
    const utmFields = TRACKABLE_FIELDS.reduce((acc, id) => {
      acc[`utm_parameter___${id}`] = params.get(`utm_${id}`) || "";
      acc[`utm_parameter___${id}_original`] = storage.getItem(`origin.urlParams.${id}`) || "";
      return acc;
    }, {});
    fetch("/.netlify/functions/hubspot-submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        formId: "startRouter",
        hutk,
        pageUri: window.location.href,
        pageName: document.title,
        fields: {
          email,
          firstname: firstName,
          lastname: lastName,
          role,
          employee_count___range: companySize,
          referred_by___generic: referredBy,
          ai_qualified: lookupOrg?.Qualify ?? "",
          company_name: lookupOrg?.Name ?? "",
          domain: lookupOrg?.Domain ?? "",
          company_offering: lookupOrg?.["Key Offering"] ?? "",
          company_industry: lookupOrg?.Industry ?? "",
          company_team: lookupOrg?.Team ?? "",
          company_ai_score: lookupOrg?.AI ?? "",
          company_open_source: lookupOrg?.["Open Source"] ?? "",
          company_social: lookupOrg?.Social ?? "",
          company_funding: lookupOrg?.Funding ?? "",
          company_web_traffic: lookupOrg?.["Web Traffic"] ?? "",
          readiness_score: lookupScore ?? "",
          page_cta_path_on_website: params.get(ATTRIBUTION_PARAM) || "",
          utm_parameter___created_date: getSimpleDate(
            `urlParams.${TRACKABLE_FIELDS.find((f) => getStashed({ id: `urlParams.${f}`, storage }))}.created`,
            storage
          ) || "",
          utm_parameter___created_date_original: getSimpleDate(
            `origin.urlParams.${TRACKABLE_FIELDS.find((f) => storage.getItem(`origin.urlParams.${f}`))}.created`,
            storage
          ) || "",
          ...utmFields,
        },
      }),
    }).catch((err) => console.error("HubSpot fallback submit failed:", err));

    deployCalendar(lookupOrg);
  };

  const doSetOrgState = (state, companySize) => {
    setOrgState(state);
    setTimeout(() => {
      console.log("Org state changed:", state, "Company Size:", companySize);

      if (state === ORG_STATES.KNOWN && !!companySize) {
        companySizeWrapperRef.current.style.display = "none";
      } else {
        companySizeWrapperRef.current.style.display = "block";
      }
    }, 1200);
  };

  return (
    <div className="relative">
      <style>{`
        #chilipiper-calendar-container {
          background-color: rgb(250, 250, 251);
        }
        #chilipiper-calendar-container iframe {
          min-height: 600px !important;
          height: 100% !important;
          border-radius: 0.75rem;
        }
      `}</style>
      {/* Fixed height container to prevent jumpiness */}
      <div className="relative" style={{ minHeight: '600px' }}>
        {/* Form - fades out when submitted */}
        <div className={formSubmitted ? "hidden pointer-events-none" : "block"}>
          {/* do not add className, HubSpot uses the DOM attributes to identify the form */}
          <DemoForm
            email={email}
            setEmail={setEmail}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            role={role}
            setRole={setRole}
            companySize={companySize}
            setCompanySize={setCompanySize}
            referredBy={referredBy}
            setReferredBy={setReferredBy}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            ready={ready}
            orgState={orgState}
            identityState={identityState}
            message={message}
            emailRef={emailRef}
            firstNameRef={firstNameRef}
            lastNameRef={lastNameRef}
            roleRef={roleRef}
            companySizeRef={companySizeRef}
            referredByRef={referredByRef}
            submitRef={submitRef}
            companySizeWrapperRef={companySizeWrapperRef}
            handleEmailBlur={handleEmailBlur}
            handleKeyDown={handleKeyDown}
            handleFocus={handleFocus}
            handleSubmit={handleSubmit}
            org={lookupOrg}
            score={lookupScore}
            storage={storage}
            emailValid={emailValid}
            hasEmailParam={hasEmailParam}
            hideTroubleLink={site?.isHomepage}
            isDownPage={isDownPage}
          />
        </div>

        <div
          id="chilipiper-calendar-container"
          className={cn(
            "rounded-xl overflow-hidden px-3 max-w-screen-sm mx-auto",
            calendarVisible ? "my-8" : null
          )}
          style={{
            minHeight: calendarVisible ? 620 : null,
            maxHeight: calendarVisible ? null : 0,
            transition: 'max-height 0.5s ease-in-out'
          }}
        >
        </div>

        <div
          id="post-form-ux-container"
          className={cn(
            "overflow-hidden flex items-center justify-center",
            (formSubmitted && (!calendarVisible || isSuccess)) ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          style={{
            maxHeight: (formSubmitted && (!calendarVisible || isSuccess)) ? 620 : 0,
            transition: 'max-height 0.5s ease-in-out, opacity 1s ease-in-out',
          }}>
          <PostFormUX
            email={email}
            firstName={firstName}
          />
        </div>
      </div>
      {(!skipIcons && !site.isHomepage) && (
        <>
          <FloatyIcon icon={faMagnifyingGlassChart} className="text-black border-black bg-dark-5 opacity-20 hidden absolute lg:flex top-0 right-20" />
          <FloatyIcon icon={faMoneyBill1Wave} className="text-black border-black bg-dark-5 opacity-20 hidden absolute lg:flex bottom-0 right-20 delay-1000" />
          <FloatyIcon icon={faRocket} className="text-black border-black bg-dark-5 opacity-20 hidden absolute lg:flex top-0 left-20 delay-1500" />
          <FloatyIcon icon={faHandshake} className="text-black border-black bg-dark-5 opacity-20 hidden absolute lg:flex bottom-0 left-20 delay-2000" />
          <FloatyIcon icon={faThumbsUp} className="text-black border-black bg-dark-5 opacity-20 hidden absolute lg:flex top-1/2 right-0 -mt-10 delay-2500" />
          <FloatyIcon icon={faPeopleGroup} className="text-black border-black bg-dark-5 opacity-20 hidden absolute lg:flex top-1/2 left-0 -mt-10 delay-500" />
        </>
      )}
    </div>
  );
};

const DemoForm = ({
  email, setEmail, firstName, setFirstName, lastName, setLastName, hideTroubleLink,
  role, setRole, companySize, setCompanySize, referredBy, setReferredBy, focusedField,
  ready, orgState, identityState, message, emailRef, firstNameRef, companySizeWrapperRef,
  lastNameRef, roleRef, companySizeRef, referredByRef, submitRef, handleEmailBlur, emailValid,
  handleKeyDown, handleFocus, handleSubmit, org, score, storage, hasEmailParam, isDownPage,
}) => (
  <form id="startRouter" onSubmit={handleSubmit} data-hs-do-not-collect="true">
    <div className="max-w-xl mx-auto">
      <div className="mb-6 pt-2">
        <FormLabel htmlFor="email" focused={focusedField === 0} lowOpacity={true} hasValue={!!email} invalid={emailValid === EMAIL_STATES.INVALID}>
          Work email *
        </FormLabel>
        <input
          ref={emailRef}
          autoFocus={!isDownPage && !hasEmailParam}
          type="email"
          id="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => handleFocus(0)}
          onBlur={handleEmailBlur}
          onKeyDown={(e) => handleKeyDown(e, 0)}
          className={cn(
            "w-full p-2 rounded-lg transition-all duration-200",
            "bg-white text-dark-80 border-4 placeholder-dark-60",
            "outline-none focus:ring-0 outline-none",
            focusedField === 0 ? "border-green-emerald" : "border-transparent",
            emailValid === EMAIL_STATES.INVALID ? "border-orange-500" : null
          )}
          placeholder="Enter your work email"
          required={true}
          style={{ colorScheme: "light" }}
          autoComplete="email"
        />
      </div>

      <div
        className={cn(
          "mb-6 transition-all duration-1000 ease-in-out",
          identityState === ID_STATES.NULL ? "opacity-100 max-h-24" :
            identityState === ID_STATES.UNKNOWN ? "opacity-100 max-h-24" :
              "opacity-0 max-h-0 overflow-hidden"
        )}
      >
        <FormLabel htmlFor="role" focused={focusedField === 1} lowOpacity={true} hasValue={!!role}>
          Role *
        </FormLabel>
        <select
          ref={roleRef}
          id="role"
          name="role"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
          }}
          onFocus={() => handleFocus(1)}
          onKeyDown={(e) => handleKeyDown(e, 1)}
          className={cn(
            "w-full p-2 rounded-lg transition-all duration-200 border-4 placeholder-dark-60",
            "outline-none focus:ring-0",
            focusedField === 1 ? "border-green-emerald" : "border-transparent",
            focusedField === 1 || role ? "text-dark-80" : "text-dark-60"
          )}
          required={true}
          style={{ colorScheme: "light" }}
          tabIndex={identityState === ID_STATES.KNOWN ? -1 : 0}
        >
          <option disabled value="">What's your role?</option>
          {ROLE_OPTIONS.map((roleOption) => (
            <option key={roleOption} value={roleOption}>
              {roleOption}
            </option>
          ))}
        </select>
      </div>

      <div
        className={cn(
          "grid grid-cols-2 gap-4 transition-all duration-1000 ease-in-out",
          identityState === ID_STATES.NULL ? "opacity-100 max-h-96 mb-6" :
            identityState === ID_STATES.UNKNOWN ? "opacity-100 max-h-96 mb-6" :
              "opacity-0 max-h-0 overflow-hidden"
        )}
      >
        <div>
          <FormLabel htmlFor="firstName" focused={focusedField === 2} lowOpacity={true} hasValue={!!firstName}>
            First name *
          </FormLabel>
          <input
            ref={firstNameRef}
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onFocus={() => handleFocus(2)}
            onKeyDown={(e) => handleKeyDown(e, 2)}
            className={cn(
              "w-full p-2 rounded-lg transition-all duration-200",
              "bg-white text-dark-80 border-4 placeholder-dark-60",
              "outline-none focus:ring-0 outline-none",
              focusedField === 2 ? "border-green-emerald" : "border-transparent"
            )}
            placeholder="First name"
            required={true}
            style={{ colorScheme: "light" }}
            autoComplete="given-name"
            tabIndex={identityState === ID_STATES.KNOWN ? -1 : 0}
          />
        </div>
        <div>
          <FormLabel htmlFor="lastName" focused={focusedField === 3} lowOpacity={true} hasValue={!!lastName}>
            Last name *
          </FormLabel>
          <input
            ref={lastNameRef}
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onFocus={() => handleFocus(3)}
            onKeyDown={(e) => handleKeyDown(e, 3)}
            className={cn(
              "w-full p-2 rounded-lg transition-all duration-200",
              "bg-white text-dark-80 border-4 placeholder-dark-60",
              "outline-none focus:ring-0 outline-none",
              focusedField === 3 ? "border-green-emerald" : "border-transparent"
            )}
            placeholder="Last name"
            required={true}
            style={{ colorScheme: "light" }}
            autoComplete="family-name"
            tabIndex={identityState === ID_STATES.KNOWN ? -1 : 0}
          />
        </div>
      </div>

      <div className="mb-6">
        <FormLabel htmlFor="referred_by___generic" focused={focusedField === 4} lowOpacity={true} hasValue={!!referredBy}>
          How did you hear about us? *
        </FormLabel>
        <input
          ref={referredByRef}
          type="text"
          id="referred_by___generic"
          name="referred_by___generic"
          value={referredBy}
          onChange={(e) => {
            setReferredBy(e.target.value);
          }}
          onFocus={() => handleFocus(4)}
          onKeyDown={(e) => handleKeyDown(e, 4)}
          className={cn(
            "w-full p-2 rounded-lg transition-all duration-200",
            "bg-white text-dark-80 border-4 placeholder-dark-60",
            "outline-none focus:ring-0",
            focusedField === 4 ? "border-green-emerald" : "border-transparent"
          )}
          placeholder="Tell us how you discovered Common Room"
          required={true}
          style={{ colorScheme: "light" }}
        />
      </div>

      <div
        className={cn(
          "transition-all duration-8000 ease-in-out",
          orgState === ORG_STATES.NULL && (focusedField !== 5) && !companySize ? "opacity-50 max-h-96" :
            orgState === ORG_STATES.KNOWN && !!companySize ? "opacity-0 max-h-0" :
              "opacity-100 max-h-24 mb-6"
        )}
        style={{
          filter: orgState === ORG_STATES.NULL && (focusedField !== 5) && !companySize ? "blur(2px)" : "none"
        }}
        ref={companySizeWrapperRef}
      >
        <FormLabel htmlFor="employee_count___range" focused={focusedField === 5} lowOpacity={true} hasValue={!!companySize}>
          Company size (employees) *
        </FormLabel>
        <select
          ref={companySizeRef}
          id="employee_count___range"
          name="employee_count___range"
          value={companySize}
          onChange={(e) => {
            setCompanySize(e.target.value);
          }}
          onFocus={() => handleFocus(5)}
          onKeyDown={(e) => handleKeyDown(e, 5)}
          className={cn(
            "w-full p-2 rounded-lg transition-all duration-200 border-4 placeholder-dark-60",
            "outline-none focus:ring-0",
            focusedField === 5 ? "border-green-emerald" : "border-transparent",
            focusedField === 5 || orgState === ORG_STATES.NULL || companySize ? "text-dark-80" : "text-dark-60"

          )}
          required={true}
          style={{ colorScheme: "light" }}
          tabIndex={orgState === ORG_STATES.KNOWN ? -1 : 0}
        >
          <option disabled value="">Company size</option>
          {COMPANY_SIZE_OPTIONS.map((sizeOption) => (
            <option key={sizeOption} value={sizeOption}>
              {sizeOption}
            </option>
          ))}
        </select>
      </div>

      <Message message={message} className="mt-8 mb-2" />

      <div className="text-center mb-12">
        <button
          ref={submitRef}
          type="submit"
          className={cn(
            "w-1/2 mx-auto text-lg text-black py-3 px-6 font-semibold rounded-xl transition-colors duration-300 focus:outline-none focus:ring-0",
            ready
              ? "bg-green-emerald scale-100 hover:scale-105 transform transition-size duration-300"
              : "bg-dark-20 cursor-not-allowed opacity-80"
          )}
          disabled={!ready}
        >
          {(ready || orgState !== ORG_STATES.NULL) ? BUTTON_SUBMIT : BUTTON_NOT_READY}
        </button>
      </div>

      <Org org={org} orgState={orgState} />

      <HiddenFields storage={storage} org={org} score={score} />
    </div>
    <p className="text-center text-sm text-dark-30 hover:text-black transition-colors duration-300 mt-4">
      Agree to {" "}
      <a
        href="/terms-of-use/"
        className="hover:underline"
        target="_blank"
      >
        terms
      </a>{" "}
      and{" "}
      <a
        href="/privacy-policy/"
        className="hover:underline"
        target="_blank"
      >
        privacy policy
      </a>.
    </p>
    {!hideTroubleLink && (
      <p className="text-center mt-1">
        <a
          href="mailto:marketing@commonroom.io"
          target="_blank"
          className="text-sm text-dark-30 hover:text-black hover:underline transition-colors duration-300"
        >
          Having trouble?
        </a>
      </p>
    )}
  </form>
);

export const Message = ({ message, className = "" }) => (
  <div
    className={
      cn("text-center pb-4 text-lemon text-lg animate-fade-in", className)
    }
  >
    {message}
  </div>
);

export const FormLabel = ({
  htmlFor,
  children,
  className = "",
  focused = false,
  lowOpacity = false,
  invalid = false,
  hasValue = false,
  ...props
}) => {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        "block text-xs mb-1 transition-colors duration-300",
        focused
          ? "text-black"
          : hasValue ? "text-dark-60 hover:text-dark-60"
            : lowOpacity
              ? "text-dark-40 opacity-40 hover:opacity-100 hover:text-dark-30"
              : "text-dark-40 hover:text-dark-60",
        invalid ? "text-orange-500" : "",
        className,
      )}
      {...props}
    >
      {children}
    </label>
  );
};

export const HiddenFields = ({ storage, org, score }) => {
  const site = useContext(SiteContext);
  const [urlParams, setUrlParams] = useState(null);
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setUrlParams(
      window && site.isActive("postStashedParams") ? params : false
    );
    setShowHidden(params.has("show"));
  }, []);

  return (
    <div className={cn("flex-col gap-4 mt-12", showHidden ? "flex" : "hidden")}>
      <div className="flex items-center gap-4 mx-8">
        {showHidden && (
          <FormLabel htmlFor="ai_qualified" className="text-sm flex-grow text-right">
            AI Qualified
          </FormLabel>
        )}
        <input
          name="ai_qualified"
          defaultValue={org?.Qualify ?? ""}
          className="rounded-lg bg-white text-dark-80 p-2 text-xs"
        />
      </div>

      {/* Company Lookup Fields */}
      {[
        { field: "Name", name: "company_name", label: "Company Name" },
        { field: "Domain", name: "domain", label: "Domain" },
        { field: "Key Offering", name: "company_offering", label: "Company Offering" },
        { field: "Industry", name: "company_industry", label: "Company Industry" },
        { field: "Team", name: "company_team", label: "Company Team" },
        { field: "Team", name: "employee_count___range", label: "Team Size" },
        { field: "AI", name: "company_ai_score", label: "Company AI Score" },
        { field: "Open Source", name: "company_open_source", label: "Company Open Source" },
        { field: "Social", name: "company_social", label: "Company Social" },
        { field: "Funding", name: "company_funding", label: "Company Funding" },
        { field: "Web Traffic", name: "company_web_traffic", label: "Company Web Traffic" },
        { name: "readiness_score", label: "Readiness Score", value: score },
      ].map(({ field, name, label, value }) => (
        <div key={name} className="flex items-center gap-4 mx-8">
          {showHidden && (
            <FormLabel htmlFor={name} className="text-sm flex-grow text-right">
              {label}
            </FormLabel>
          )}
          <input
            name={name}
            defaultValue={value ?? org?.[field] ?? ""}
            className="rounded-lg bg-white text-dark-80 p-2 text-xs"
          />
        </div>
      ))}
      {[].concat([
        {
          name: "page_cta_path_on_website",
          value: urlParams ? urlParams.get(ATTRIBUTION_PARAM) : "",
        },
        {
          name: "first_seen_on_website",
          storage: (storage) => getSimpleDate("origin.created", storage),
        },
        {
          name: "utm_parameter___created_date",
          storage: (storage) =>
            getSimpleDate(
              `urlParams.${TRACKABLE_FIELDS.find((f) =>
                getStashed({ id: `urlParams.${f}`, storage })
              )}.created`,
              storage
            ),
        },
        {
          name: "utm_parameter___created_date_original",
          storage: (storage) =>
            getSimpleDate(
              `origin.urlParams.${TRACKABLE_FIELDS.find((f) =>
                storage.getItem(`origin.urlParams.${f}`)
              )}.created`,
              storage
            ),
        },
      ])
        .concat(
          TRACKABLE_FIELDS.reduce((prev, id) => {
            return prev.concat([
              { name: `utm_parameter___${id}`, value: urlParams ? urlParams.get(`utm_${id}`) : "" },
              {
                name: `utm_parameter___${id}_original`,
                storage: (storage) => storage.getItem(`origin.urlParams.${id}`),
              },
            ]);
          }, [])
        ).map((field) => (
          <div key={field.name} className="flex items-center gap-4 mx-8">
            {showHidden && (
              <FormLabel htmlFor={field.name} className="text-sm flex-grow text-right">
                {field.name}
              </FormLabel>
            )}
            <input
              name={field.name}
              defaultValue={field?.value
                ? field?.value
                : field?.storage
                  ? field?.storage(storage)
                  : ""}
              className="rounded-lg bg-white text-dark-80 p-2 text-xs"
            />
          </div>
        ))}
      {[
        { name: "chatbot_engaged", value: String(countChatbotMessages()) },
        { name: "chatbot_sessions", value: String(countChatbotSessions()) },
        { name: "chatbot_goal", value: getChatbotGoal() || "" },
        { name: "chatbot_transcript", value: getChatbotTranscript() },
        { name: "chatbot_path", value: getChatbotPath() || "" },
      ].map((field) => (
        <div key={field.name} className="flex items-center gap-4 mx-8">
          {showHidden && (
            <FormLabel htmlFor={field.name} className="text-sm flex-grow text-right">
              {field.name}
            </FormLabel>
          )}
          <input
            name={field.name}
            defaultValue={field.value}
            className="rounded-lg bg-white text-dark-80 p-2 text-xs"
          />
        </div>
      ))}
    </div>
  )
};

export const Org = ({ org, orgState }) => {
  if (orgState !== ORG_STATES.KNOWN) return null;

  // Flatten the nested structure
  const flattenedOrg = {
    ...org?.basics,
    ...org?.details,
    ...org
  };

  return (
    <div className="flex flex-wrap justify-center gap-2">
      {Object.entries(FIELD_LABELS).map(([key, label]) => {
        // Check DISPLAY_FIELD_LABELS for a UI-friendly field first, then fall back to FIELD_LABELS key
        const displayEntry = Object.entries(DISPLAY_FIELD_LABELS).find(([displayKey, displayLabel]) => displayLabel === label && flattenedOrg[displayKey]);
        let value = displayEntry ? flattenedOrg[displayEntry[0]] : flattenedOrg[key];
        if (!value || [null, "", "N/A", 0, "0", "$0", "null", "Unfunded", "None"].includes(value)) return null;

        return (
          <div
            key={key}
            className="inline-flex items-center px-3 py-1 bg-dark-2 text-dark-50 text-xs rounded-md border border-dark-5"
          >
            <span className="font-medium mr-2">{label}:</span>
            <span className="text-dark-80">{value}</span>
          </div>
        );
      })}
    </div>
  );
};

const PostFormUX = ({
  className,
  email,
  firstName,
}) => {
  const domain = email.split('@')[1]?.toLowerCase();

  return (
    <a className={cn(
      "max-w-xl mx-auto my-8 p-8 lg:p-12 bg-dark-5 rounded-xl shadow-lg text-center group hover:bg-dark-2 border border-green-emerald hover:border-black transition-colors duration-300",
      className
    )}
      href={`/plays?d=${domain}`}
    >
      <div className="mb-8">
        <p className="text-xl font-medium">
          Thanks{firstName ? ` ${firstName}` : ""}.
        </p>
        <p className="mt-1 text-dark-60">
          We will reach out soon.
        </p>
      </div>
      <div className="">
        <p
          className="text-3xl text-link font-semibold group-hover:text-black duration-300">
          Start now with custom plays
        </p>
        <p className="text-dark-60 text-lg mt-4">
          Fetch outbound plays matched to your GTM motion and buying signals with our pipeline play generator.
        </p>
      </div>

    </a>
  );
};
